import {
  apiRequest,
  isClerkReverificationHint,
  type ClerkReverificationHint,
  type ReverificationConfig,
} from "./api";

type TokenResolver = (options?: {
  skipCache?: boolean;
}) => Promise<string | null>;

type PlaidItemSummaryWire = {
  consent_granted_at: string;
  id: string;
  institution_id?: string;
  institution_name: string;
  last_successful_sync_at?: string | null;
  status: string;
};

export type PlaidItemSummary = {
  consentGrantedAt: string;
  id: string;
  institutionId?: string;
  institutionName: string;
  lastSuccessfulSyncAt?: string | null;
  status: string;
};

type PlaidItemsResponse = {
  items: PlaidItemSummaryWire[];
};

// ADR-022: ALL Plaid routes (link-token, exchange, sync, items list, items
// delete) gate on MFA. The 24h factor age matches the backend's
// RequireMFA(24*time.Hour) mount in cmd/server/routes.go. Every Plaid API
// helper here MUST be wrappable in Clerk's useReverification() so a stale
// `fva` claim pops the focused secure prompt and the request auto-retries
// after the user satisfies MFA — see ADR-022 §"Decision" for the contract.
const PLAID_REVERIFICATION: ReverificationConfig = {
  afterMinutes: 24 * 60,
  level: "second_factor",
};

type PlaidApiOptions = {
  reverification?: boolean;
  secondFactorEnrolled?: boolean;
};

// SILL-304: Plaid link-token request mode. Wire literals: "initial" (default)
// or "income". "initial" sends no mode field; only "income" requires it.
export type PlaidLinkTokenMode = "initial" | "income" | "transactions";

export interface PlaidExchangeRequest {
  publicToken: string;
  institutionID: string;
  institutionName: string;
  /**
   * The Plaid Link mode the public_token was minted for. The backend
   * derives `consent_scopes` from this server-side via `ScopesForMode`
   * — clients no longer claim scopes directly. This eliminates the bug
   * class where a stale frontend stamps ungranted products into the
   * audit trail.
   */
  mode: PlaidLinkTokenMode;
}

export function getPlaidItems(
  getToken: TokenResolver,
): Promise<PlaidItemSummary[]>;
export function getPlaidItems(
  getToken: TokenResolver,
  options: PlaidApiOptions & { reverification: true },
): Promise<PlaidItemSummary[] | ClerkReverificationHint>;
export async function getPlaidItems(
  getToken: TokenResolver,
  options: PlaidApiOptions = {},
): Promise<PlaidItemSummary[] | ClerkReverificationHint> {
  const token = (await getToken({ skipCache: true })) ?? undefined;
  const response = await apiRequest<
    PlaidItemsResponse | ClerkReverificationHint
  >("/api/plaid/items", {
    allowReverificationOnSecondFactorSetupRequired:
      options.secondFactorEnrolled === true,
    reverification: options.reverification ? PLAID_REVERIFICATION : undefined,
    token,
  });
  if (isClerkReverificationHint(response)) {
    return response;
  }
  return response.items.map(toPlaidItemSummary);
}

export function disconnectPlaidItem(
  getToken: TokenResolver,
  itemID: string,
): Promise<void>;
export function disconnectPlaidItem(
  getToken: TokenResolver,
  itemID: string,
  options: PlaidApiOptions & { reverification: true },
): Promise<void | ClerkReverificationHint>;
export async function disconnectPlaidItem(
  getToken: TokenResolver,
  itemID: string,
  options: PlaidApiOptions = {},
): Promise<void | ClerkReverificationHint> {
  const token = (await getToken({ skipCache: true })) ?? undefined;
  const result = await apiRequest<void | ClerkReverificationHint>(
    `/api/plaid/items/${encodeURIComponent(itemID)}`,
    {
      allowReverificationOnSecondFactorSetupRequired:
        options.secondFactorEnrolled === true,
      method: "DELETE",
      reverification: options.reverification ? PLAID_REVERIFICATION : undefined,
      token,
    },
  );
  if (isClerkReverificationHint(result)) {
    return result;
  }
}

type CreatePlaidLinkTokenOptions = PlaidApiOptions & {
  mode?: PlaidLinkTokenMode;
};

// SILL-304: createPlaidLinkToken mints a single-use Plaid Link token. Pass
// to Plaid Link's create({ token }). mode "income" scopes to the
// income_verification product; default scopes to auth + liabilities.
// ADR-022: this route gates on MFA (24h factor age). Callers MUST wrap in
// useReverification() and pass `reverification: true` so a stale `fva`
// returns a hint shape the Clerk SDK can recover from instead of a hard
// throw. Always fetches a fresh JWT (`skipCache: true`) so the retried
// request carries the updated `fva` claim.
export function createPlaidLinkToken(
  getToken: TokenResolver,
  opts?: { mode?: PlaidLinkTokenMode },
): Promise<string>;
export function createPlaidLinkToken(
  getToken: TokenResolver,
  opts: CreatePlaidLinkTokenOptions & { reverification: true },
): Promise<string | ClerkReverificationHint>;
export async function createPlaidLinkToken(
  getToken: TokenResolver,
  opts: CreatePlaidLinkTokenOptions = {},
): Promise<string | ClerkReverificationHint> {
  const token = (await getToken({ skipCache: true })) ?? undefined;
  const body: { mode?: PlaidLinkTokenMode } =
    opts.mode && opts.mode !== "initial" ? { mode: opts.mode } : {};
  const response = await apiRequest<
    { link_token: string } | ClerkReverificationHint
  >("/api/plaid/link-token", {
    allowReverificationOnSecondFactorSetupRequired:
      opts.secondFactorEnrolled === true,
    body,
    method: "POST",
    reverification: opts.reverification ? PLAID_REVERIFICATION : undefined,
    token,
  });
  if (isClerkReverificationHint(response)) {
    return response;
  }
  return response.link_token;
}

// SILL-304: exchangePlaidPublicToken trades the public_token Plaid Link
// returns on onSuccess for an internal item id. Institution metadata is
// persisted server-side so list endpoints can render the bank name without
// an extra Plaid round-trip.
// ADR-022: MFA-gated — same reverification contract as createPlaidLinkToken.
// incomeProcessing is always present in the return value (false when the
// backend omits the field — i.e. non-income flows or income flows where
// the backend completed the fetch inline).
export function exchangePlaidPublicToken(
  getToken: TokenResolver,
  req: PlaidExchangeRequest,
): Promise<{ itemID: string; incomeProcessing: boolean }>;
export function exchangePlaidPublicToken(
  getToken: TokenResolver,
  req: PlaidExchangeRequest,
  options: PlaidApiOptions & { reverification: true },
): Promise<
  { itemID: string; incomeProcessing: boolean } | ClerkReverificationHint
>;
export async function exchangePlaidPublicToken(
  getToken: TokenResolver,
  req: PlaidExchangeRequest,
  options: PlaidApiOptions = {},
): Promise<
  { itemID: string; incomeProcessing: boolean } | ClerkReverificationHint
> {
  const token = (await getToken({ skipCache: true })) ?? undefined;
  const response = await apiRequest<
    { item_id: string; income_processing?: boolean } | ClerkReverificationHint
  >("/api/plaid/exchange", {
    allowReverificationOnSecondFactorSetupRequired:
      options.secondFactorEnrolled === true,
    body: {
      public_token: req.publicToken,
      institution_id: req.institutionID,
      institution_name: req.institutionName,
      mode: req.mode,
    },
    method: "POST",
    reverification: options.reverification ? PLAID_REVERIFICATION : undefined,
    token,
  });
  if (isClerkReverificationHint(response)) {
    return response;
  }
  return {
    itemID: response.item_id,
    incomeProcessing: Boolean(response.income_processing),
  };
}

// SILL-304: syncPlaidItem triggers a fresh balance + liabilities sync for a
// single linked item — wired to the per-institution Refresh button AND
// called automatically right after exchange so liabilities are visible
// without a manual refresh.
// ADR-022: MFA-gated — same reverification contract as the other helpers.
export function syncPlaidItem(
  getToken: TokenResolver,
  itemID: string,
): Promise<void>;
export function syncPlaidItem(
  getToken: TokenResolver,
  itemID: string,
  options: PlaidApiOptions & { reverification: true },
): Promise<void | ClerkReverificationHint>;
export async function syncPlaidItem(
  getToken: TokenResolver,
  itemID: string,
  options: PlaidApiOptions = {},
): Promise<void | ClerkReverificationHint> {
  const token = (await getToken({ skipCache: true })) ?? undefined;
  const result = await apiRequest<void | ClerkReverificationHint>(
    "/api/plaid/sync",
    {
      allowReverificationOnSecondFactorSetupRequired:
        options.secondFactorEnrolled === true,
      body: { item_id: itemID },
      method: "POST",
      reverification: options.reverification ? PLAID_REVERIFICATION : undefined,
      token,
    },
  );
  if (isClerkReverificationHint(result)) {
    return result;
  }
}

function toPlaidItemSummary(item: PlaidItemSummaryWire): PlaidItemSummary {
  return {
    consentGrantedAt: item.consent_granted_at,
    id: item.id,
    institutionId: item.institution_id,
    institutionName: item.institution_name,
    lastSuccessfulSyncAt: item.last_successful_sync_at,
    status: item.status,
  };
}
